/**
 * A weapon as the strike pipeline sees it (Foundry: item/weapon/document.ts): an equipped weapon
 * Entry, the basic unarmed attack, or the weapon a Strike Rule Element or a BattleForm constructs.
 * Carries the derived facts Foundry prepares on a weapon (melee or ranged, striking dice from
 * runes, the magical trait, alternate usages) and the roll options predicates test.
 */
import { abpStrikingDice } from "./abp";
import type { Entry } from "../../shared/snapshot-format";
import { EnginePhysical, isEquippedFor, type CarryType, type EnginePhysicalInput, type ItemMaterial } from "./physical";
import type { RuleInstance } from "./rule-elements/base";
import { sluggify } from "./slug";
import { ATTRIBUTES, DAMAGE_TYPE_CATEGORIES, EQUIVALENT_WEAPONS, MANDATORY_RANGED_GROUPS, MELEE_ONLY_TRAITS, RANGED_ONLY_TRAITS, THROWN_BASE_WEAPONS, isAttribute, isDamageDieSize, isDamageType, nextDieSize, type Attribute, type DamageDieSize } from "./values";

export interface WeaponRunes {
  potency: number;
  striking: number;
  property: string[];
}

export interface WeaponDamageData {
  dice: number;
  die: DamageDieSize | null;
  damageType: string;
  modifier: number;
  persistent: { number: number; faces: number | null; type: string } | null;
}

export type { CarryType };

/** A weapon fed to the engine directly (tests and fixtures); the inventory (#11) is the app's source. */
export interface EquippedWeaponInput {
  /** A weapon Entry from the equipment Pack. */
  entry: Entry;
  carryType?: CarryType;
  handsHeld?: 0 | 1 | 2;
  /** Whether an invested item is invested; null when it needs no investment. */
  invested?: boolean | null;
  quantity?: number;
  /** The player's trait choices: a versatile damage type, a modular configuration, a double-barrel toggle. */
  traitToggles?: { versatile?: string | null; modular?: number | null; doubleBarrel?: boolean };
  /** Runes etched on this copy (a +1 striking longsword), over the Entry's own. */
  runes?: Partial<WeaponRunes>;
  material?: ItemMaterial;
  /** A stable id for this copy when the same Entry is carried twice. */
  id?: string;
}

/** The parts of a weapon's `system` this module reads and writes. */
interface WeaponSystem {
  category?: string | null;
  group?: string | null;
  baseItem?: string | null;
  damage?: { dice?: number; die?: DamageDieSize | null; damageType?: string; modifier?: number; persistent?: unknown };
  range?: number | null;
  maxRange?: number | null;
  reload?: { value?: string | null };
  traits?: { value?: string[]; otherTags?: string[]; rarity?: string; toggles?: Record<string, unknown> };
  runes?: Partial<WeaponRunes>;
  material?: { type?: string | null; grade?: string | null };
  usage?: { value?: string; canBeAmmo?: boolean };
  equipped?: { carryType?: string; handsHeld?: number; invested?: boolean | null; inSlot?: boolean };
  bonus?: { value?: number };
  bonusDamage?: { value?: number };
  splashDamage?: { value?: number };
  attribute?: string | null;
  graspingAppendage?: boolean;
  quantity?: number;
  meleeUsage?: { damage?: { type?: string; die?: string }; group?: string | null; traits?: string[]; traitToggles?: { modular?: number | null; versatile?: string | null } } | null;
  grade?: string | null;
  specific?: unknown;
  options?: { value?: string[] };
  [key: string]: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export interface EngineWeaponInput extends EnginePhysicalInput {
  traitToggles?: EquippedWeaponInput["traitToggles"] | undefined;
  /** The Strike Rule Element that constructed this weapon, when one did. */
  rule?: RuleInstance | null | undefined;
  battleForm?: boolean | undefined;
  fist?: boolean | undefined;
  /** A fixed attack modifier (battle forms): kept for reference, characters compute their own. */
  fixedAttack?: number | null | undefined;
}

/** A weapon Entry stub for the basic unarmed attack and for synthetic strikes. */
export function weaponEntry(id: string, name: string, slug: string, system: Record<string, unknown>, uuid = `synthetic.weapon.${id}`): Entry {
  return {
    uuid,
    id,
    pack: "",
    type: "weapon",
    slug,
    name,
    img: "",
    folder: [],
    level: typeof (system.level as { value?: unknown } | undefined)?.value === "number" ? ((system.level as { value: number }).value) : null,
    rarity: (system.traits as { rarity?: string } | undefined)?.rarity ?? "common",
    traits: ((system.traits as { value?: string[] } | undefined)?.value ?? []).slice(),
    publication: { title: "", license: "", remaster: true },
    system,
  };
}

export class EngineWeapon extends EnginePhysical {
  declare readonly system: WeaponSystem;
  traitToggles: { versatile: string | null; modular: number | null; doubleBarrel: boolean };
  /** The Strike Rule Element that made this weapon, if any. */
  readonly rule: RuleInstance | null;
  readonly battleForm: boolean;
  readonly fist: boolean;
  readonly fixedAttack: number | null;
  /** Given this weapon is an alternative usage, whether it is melee or thrown. */
  altUsageType: "melee" | "thrown" | null = null;
  comboSibling: EngineWeapon | null = null;
  comboMeleeUsage = false;
  /** Foundry's `flags.pf2e.damageFacesUpgraded`: the base die may be upgraded once. */
  damageFacesUpgraded = false;
  /** The best item bonus to attack rolls (potency rune, weapon bonus). */
  attackItemBonus = 0;
  /** The dice count the Entry itself has, before striking runes. */
  readonly inherentDice: number;
  /** The ammunition stack selected for this weapon, set by the inventory. */
  ammoItem: EnginePhysical | null = null;

  constructor(input: EngineWeaponInput) {
    super(input);
    const system = this.system;
    const toggles = input.traitToggles ?? {};
    const sourceToggles = (system.traits?.toggles ?? {}) as { versatile?: { selected?: unknown }; modular?: { selected?: unknown }; doubleBarrel?: { selected?: unknown } };
    this.traitToggles = {
      versatile: toggles.versatile ?? (typeof sourceToggles.versatile?.selected === "string" ? sourceToggles.versatile.selected : null),
      modular: toggles.modular ?? (typeof sourceToggles.modular?.selected === "number" ? sourceToggles.modular.selected : null),
      doubleBarrel: toggles.doubleBarrel ?? sourceToggles.doubleBarrel?.selected === true,
    };
    this.rule = input.rule ?? null;
    this.battleForm = input.battleForm === true;
    this.fist = input.fist === true;
    this.fixedAttack = input.fixedAttack ?? null;
    this.inherentDice = typeof system.damage?.dice === "number" ? system.damage.dice : 1;
    this.prepareBaseData();
    this.prepareDerivedData();
  }

  /* --------------------------------- Accessors --------------------------------- */

  override get category(): string {
    return typeof this.system.category === "string" && this.system.category ? this.system.category : "simple";
  }
  override get group(): string | null {
    return typeof this.system.group === "string" && this.system.group ? this.system.group : null;
  }
  override get baseType(): string | null {
    return typeof this.system.baseItem === "string" && this.system.baseItem ? this.system.baseItem : null;
  }
  override get traits(): string[] {
    return this.system.traits?.value ?? [];
  }
  override get otherTags(): string[] {
    return this.system.traits?.otherTags ?? [];
  }
  get damage(): WeaponDamageData {
    const d = this.system.damage ?? {};
    return {
      dice: typeof d.dice === "number" ? d.dice : 0,
      die: isDamageDieSize(d.die) ? d.die : null,
      damageType: typeof d.damageType === "string" && d.damageType ? d.damageType : "bludgeoning",
      modifier: typeof d.modifier === "number" ? d.modifier : 0,
      persistent: isRecord(d.persistent) && typeof d.persistent.number === "number" ? { number: d.persistent.number, faces: typeof d.persistent.faces === "number" ? d.persistent.faces : null, type: String(d.persistent.type ?? "untyped") } : null,
    };
  }
  /** The default attribute used in attack rolls. */
  get defaultAttribute(): Attribute {
    const attribute = this.system.attribute;
    return isAttribute(attribute) ? attribute : this.isRanged ? "dex" : "str";
  }
  get hands(): "0" | "1" | "1+" | "2" {
    const usage = this.system.usage?.value ?? "held-in-one-hand";
    const map: Record<string, "0" | "1" | "1+" | "2"> = { worngloves: "0", "held-in-one-hand": "1", "held-in-one-plus-hands": "1+", "held-in-two-hands": "2" };
    return map[usage] ?? "1";
  }
  get range(): { increment: number | null; max: number } | null {
    const increment = typeof this.system.range === "number" && this.system.range > 0 ? this.system.range : null;
    const maxRange = typeof this.system.maxRange === "number" && this.system.maxRange > 0 ? this.system.maxRange : null;
    return maxRange ? { increment: null, max: maxRange } : increment ? { increment, max: increment * 6 } : null;
  }
  get reload(): string | null {
    const value = this.system.reload?.value;
    return typeof value === "string" && value ? value : null;
  }
  get isRanged(): boolean {
    return typeof this.system.range === "number" && this.system.range > 0;
  }
  get isMelee(): boolean {
    return !this.isRanged;
  }
  /** Whether the weapon in its current usage is thrown. */
  get isThrown(): boolean {
    return this.isRanged && ((THROWN_BASE_WEAPONS as readonly string[]).includes(this.baseType ?? "") || this.traits.includes("thrown"));
  }
  override get isEquipped(): boolean {
    if (this.category === "unarmed" && !this.otherTags.includes("handwraps-of-mighty-blows")) return true;
    return isEquippedFor(this.usage, { carryType: this.carryType, handsHeld: this.handsHeld, inSlot: this.inSlot }) || (this.handsHeld === 1 && this.traits.some((t) => /^jousting-d\d{1,2}$/.test(t)));
  }
  get graspingAppendage(): boolean {
    return this.system.graspingAppendage === true;
  }
  get splashDamage(): number {
    return typeof this.system.splashDamage?.value === "number" ? this.system.splashDamage.value : 0;
  }
  /** The weapon's damage before modification, with the trait toggles' damage type. */
  get baseDamage(): WeaponDamageData {
    const damage = this.damage;
    const modular = this.modularConfig;
    return { ...damage, damageType: this.traitToggles.versatile ?? modular?.damageType ?? damage.damageType };
  }
  get modularConfig(): { damageType: string; traits: string[] } | null {
    if (!this.traits.includes("modular")) return null;
    const configured = (this.system.traits as { config?: { modular?: { damageType: string; traits: string[] }[] } } | undefined)?.config?.modular;
    const options = configured?.length ? configured : [{ damageType: "bludgeoning", traits: [] }, { damageType: "piercing", traits: [] }, { damageType: "slashing", traits: [] }];
    return options[this.traitToggles.modular ?? 0] ?? options[0] ?? null;
  }
  /** Selectable damage types among versatile (and modular) traits, the base type excluded. */
  get versatileOptions(): string[] {
    const types = this.traits.filter((t) => t.startsWith("versatile") || t === "modular").flatMap((trait): string[] => {
      if (trait === "modular") return ["bludgeoning", "piercing", "slashing"];
      const short = /^versatile-(\w+)$/.exec(trait)?.[1];
      const damageType = short === "b" ? "bludgeoning" : short === "p" ? "piercing" : short === "s" ? "slashing" : short;
      return isDamageType(damageType) ? [damageType] : [];
    });
    return [...new Set(types)].filter((t) => this.damage.damageType !== t);
  }
  get dealsDamage(): boolean {
    const base = this.baseDamage;
    return base.dice > 0 || base.modifier > 0 || this.splashDamage > 0 || !!base.persistent?.number;
  }
  get isOversized(): boolean {
    return false;
  }

  /* ------------------------------- Preparation ------------------------------- */

  /** Foundry's WeaponPF2e#prepareBaseData and #prepareTraits, for the parts that affect a strike. */
  private prepareBaseData(): void {
    const system = this.system;
    system.category ||= "simple";
    system.group ||= null;
    system.baseItem ||= null;
    system.damage ??= {};
    if (!isDamageDieSize(system.damage.die)) system.damage.die = null;
    system.damage.modifier ??= 0;
    system.damage.dice ??= 1;
    if (!system.damage.die && system.damage.dice > 0) system.damage.modifier ||= system.damage.dice;
    const traits = { ...(system.traits ?? {}), value: [...(system.traits?.value ?? [])], otherTags: [...(system.traits?.otherTags ?? [])] } as { value: string[]; otherTags: string[]; rarity?: string; toggles?: Record<string, unknown> };
    system.traits = traits;
    system.runes = { potency: this.runes.potency, striking: this.runes.striking, property: this.runes.property };
    system.graspingAppendage = ["fist", "claw"].includes(this.baseType ?? "") ? true : this.category === "unarmed" ? !!system.graspingAppendage : false;
    if (system.attribute && !isAttribute(system.attribute)) system.attribute = null;

    if (system.category === "unarmed" && !traits.value.includes("unarmed")) traits.value.push("unarmed");
    if (this.slug === "handwraps-of-mighty-blows" && !traits.otherTags.includes("handwraps-of-mighty-blows")) traits.otherTags.push("handwraps-of-mighty-blows");

    const mandatoryRanged = (system.group && (MANDATORY_RANGED_GROUPS as readonly string[]).includes(system.group)) || ["combination", "thrown"].some((t) => traits.value.includes(t));
    if (mandatoryRanged) system.range ??= 10;
    const mandatoryMelee = !mandatoryRanged && traits.value.some((t) => /^thrown-\d{1,3}$/.test(t));
    if (mandatoryMelee) system.range = null;
    if (this.isMelee) traits.value = traits.value.filter((t) => !(RANGED_ONLY_TRAITS as readonly string[]).includes(t) && !t.startsWith("volley"));
    else traits.value = traits.value.filter((t) => !(MELEE_ONLY_TRAITS as readonly string[]).includes(t) && !t.startsWith("thrown-"));

    if (traits.value.includes("consumable")) system.runes = { potency: 0, striking: 0, property: [] };
    const runes = this.runes;
    if (!this.isMagical && (runes.potency > 0 || runes.striking > 0 || runes.property.length > 0)) traits.value.push("magical");
    traits.value = [...new Set(traits.value)].sort();
    this.attackItemBonus = Math.max(runes.potency, typeof system.bonus?.value === "number" ? system.bonus.value : 0, 0);

    // Striking rune, or Automatic Bonus Progression's devastating attacks (#12): more damage dice instead of one.
    const inherentDiceNumber = system.damage.die ? this.inherentDice : 0;
    const strikingDice = this.abpLevel !== null ? abpStrikingDice(this.abpLevel) : runes.striking;
    if (inherentDiceNumber === 1 && !this.battleForm) system.damage.dice = Math.max(system.damage.dice, inherentDiceNumber + strikingDice);

    // Modular configuration traits.
    const config = this.modularConfig;
    if (config) for (const trait of config.traits) if (!traits.value.includes(trait)) traits.value.push(trait);

    // Double-barrel toggle: one die size up, once.
    if (traits.value.includes("double-barrel") && this.isRanged && !this.isThrown && this.traitToggles.doubleBarrel && !this.damageFacesUpgraded && system.damage.die) {
      system.damage.die = nextDieSize(system.damage.die as DamageDieSize, "upgrade");
      const fatal = traits.value.find((t) => /^fatal-d\d{1,2}$/.test(t));
      if (fatal) traits.value.splice(traits.value.indexOf(fatal), 1, upgradeWeaponTrait(fatal));
    }
  }

  /** Foundry's `processTwoHandTrait`: a two-hand-dN trait raises the die when held in two hands. */
  processTwoHandTrait(): void {
    const twoHandFaces = Number(this.traits.find((t) => t.startsWith("two-hand-d"))?.replace("two-hand-d", ""));
    const diceFaces = Number(this.system.damage?.die?.replace("d", ""));
    if (this.handsHeld === 2 && [4, 6, 8, 10, 12].includes(twoHandFaces) && twoHandFaces > diceFaces) this.system.damage!.die = `d${twoHandFaces}` as DamageDieSize;
  }

  /** Foundry's PCAttackTraitHelpers.adjustWeapon: fatal-aim held in two hands, jousting in one. */
  adjustForGrip(): void {
    const traits = this.system.traits!.value!;
    for (const trait of [...traits]) {
      switch (trait.replace(/-d?\d{1,3}$/, "")) {
        case "fatal-aim": {
          if (this.range?.increment && this.handsHeld === 2) {
            const fatal = trait.replace("-aim", "");
            if (!traits.includes(fatal)) traits.push(fatal);
          }
          break;
        }
        case "jousting": {
          if (this.handsHeld === 1) {
            const die = /(d\d{1,2})$/.exec(trait)?.[1];
            if (isDamageDieSize(die)) this.system.damage!.die = die;
          }
          break;
        }
        default:
          break;
      }
    }
  }

  /* ------------------------------- Roll options ------------------------------- */

  override getRollOptions(prefix = this.type, includeGranter = true): string[] {
    const base = this.baseDamage;
    const faces = Number(base.die?.replace(/^d/, ""));
    const category = DAMAGE_TYPE_CATEGORIES[base.damageType] ?? null;
    const thrownMelee = this.isThrown && this.altUsageType === "thrown";
    const baseTypes = [this.baseType, EQUIVALENT_WEAPONS[this.baseType ?? ""]].filter((t): t is string => !!t);
    const entries: [string, boolean][] = [
      [`category:${this.category}`, true],
      [`group:${this.group ?? "none"}`, true],
      ...baseTypes.map((t): [string, boolean] => [`base:${t}`, true]),
      [`bulk:${this.bulkLabel}`, true],
      [`usage:hands:${this.hands}`, this.hands !== "0"],
      [`range-increment:${this.range?.increment}`, !!this.range?.increment],
      [`reload:${this.reload}`, !!this.reload],
      [`damage:type:${base.damageType}`, true],
      [`damage:category:${category}`, !!category],
      [`damage:die:number:${base.die ? base.dice : 0}`, !!faces],
      [`damage:die:faces:${faces}`, !!faces],
      [`damage-dice:${base.die ? base.dice : 0}`, !!faces],
      [`damage:persistent:${base.persistent?.type}`, !!base.persistent],
      ["melee", this.isMelee],
      ["ranged", this.isRanged],
      ["thrown", this.isThrown],
      ["thrown-melee", thrownMelee],
      ...this.runes.property.map((p): [string, boolean] => [`rune:property:${sluggify(p)}`, true]),
      [`rune:potency:${this.runes.potency}`, this.runes.potency > 0],
      [`rune:striking:${this.runes.striking}`, this.runes.striking > 0],
    ];
    const options = super.getRollOptions(prefix, includeGranter);
    options.push(...entries.filter((e) => e[1]).map((e) => `${prefix}:${e[0]}`));
    const ammo = this.ammoItem;
    if (ammo) {
      const material = ammo.material;
      options.push(`ammo:id:${ammo.id}`, `ammo:slug:${ammo.slug}`, `ammo:level:${ammo.itemLevel}`, ...ammo.traits.map((t) => `ammo:trait:${t}`));
      if (material.type) options.push(`ammo:material:type:${material.type}`);
      if (material.grade) options.push(`ammo:material:grade:${material.grade}`);
    }
    if (this.comboSibling) options.push(...this.comboSibling.getRollOptions(`${prefix}:${this.comboSibling.isRanged ? "ranged-usage" : "melee-usage"}`));
    return options;
  }

  private get bulkLabel(): string {
    const bulk = (this.system.bulk as { value?: unknown } | undefined)?.value;
    const value = typeof bulk === "number" ? bulk : 0;
    return value === 0 ? "negligible" : value < 1 ? "light" : String(value);
  }

  /* ----------------------------- Alternate usages ----------------------------- */

  /** Foundry's getAltUsages: the thrown usage of a thrown melee weapon and the melee usage of a combination weapon. */
  getAltUsages(recurse = true): EngineWeapon[] {
    const melee = this.toMeleeUsage();
    const usages = [this.toThrownUsage(), melee, recurse ? melee?.toThrownUsage() : null].filter((w): w is EngineWeapon => !!w);
    return usages;
  }

  private cloneWith(overlay: (system: WeaponSystem) => void, altUsage: "melee" | "thrown"): EngineWeapon {
    const system = structuredClone(this.system);
    overlay(system);
    const entry = { ...this.entry, system: system as Record<string, unknown>, traits: system.traits?.value ?? [] };
    const clone = new EngineWeapon({
      entry,
      answers: this.answers,
      choiceKey: this.choiceKey,
      levelTaken: this.levelTaken,
      grantedBy: this.grantedBy,
      inventoryId: this.inventoryId,
      carryType: this.carryType,
      handsHeld: this.handsHeld,
      inSlot: this.inSlot ?? undefined,
      invested: this.invested,
      quantity: this.quantity,
      containerId: this.containerId,
      abpLevel: this.abpLevel,
      size: this.size,
      ammo: this.ammo,
      traitToggles: altUsage === "melee" ? { modular: system.meleeUsage?.traitToggles?.modular ?? null, versatile: system.meleeUsage?.traitToggles?.versatile ?? null } : this.traitToggles,
      rule: this.rule,
      battleForm: this.battleForm,
      fist: this.fist,
      fixedAttack: this.fixedAttack,
    });
    clone.altUsageType = altUsage;
    const comboSibling = this.traits.includes("combination") ? this : this.comboSibling;
    if (comboSibling) clone.comboSibling = comboSibling;
    clone.name = this.name;
    return clone;
  }

  private toThrownUsage(): EngineWeapon | null {
    const thrownTrait = this.traits.find((t) => /^thrown-\d{1,3}$/.test(t));
    if (this.isRanged || !thrownTrait) return null;
    const range = Number(/(\d{1,3})$/.exec(thrownTrait)?.[1]);
    return this.cloneWith((system) => {
      system.range = range;
      const traits = [...(system.traits?.value ?? [])];
      traits.splice(traits.indexOf(thrownTrait), 1, "thrown");
      system.traits = { ...system.traits, value: traits };
    }, "thrown");
  }

  private toMeleeUsage(): EngineWeapon | null {
    const meleeUsage = this.system.meleeUsage;
    if (!meleeUsage || this.comboMeleeUsage) return null;
    const clone = this.cloneWith((system) => {
      const die = meleeUsage.damage?.die;
      system.damage = { damageType: meleeUsage.damage?.type ?? "bludgeoning", dice: 1, die: isDamageDieSize(die) ? die : "d4", modifier: 0 };
      system.group = meleeUsage.group ?? null;
      system.range = null;
      system.reload = { value: null };
      system.traits = { ...system.traits, value: [...(meleeUsage.traits ?? [])] };
    }, "melee");
    clone.comboMeleeUsage = true;
    return clone;
  }
}

/** Upgrade a trait with a die annotation ("fatal-d10" -> "fatal-d12"), or return it unchanged. */
export function upgradeWeaponTrait(trait: string): string {
  const match = /-d(4|6|8|10|12)$/.exec(trait);
  const value = Number(match?.[1]);
  if ([4, 6, 8, 10, 12].includes(value)) return trait.replace(new RegExp(String.raw`d${value}$`), nextDieSize(`d${value}` as DamageDieSize, "upgrade"));
  return trait;
}

/** Foundry's `addOrUpgradeTrait` for a plain trait list: an annotated trait replaces a lesser one of its base. */
export function addOrUpgradeTrait(traits: string[], newTrait: string): void {
  const annotated = /^([a-z][-a-z]+)-(\d*d?\d+)$/.exec(newTrait);
  if (!annotated) {
    if (!traits.includes(newTrait)) traits.push(newTrait);
    return;
  }
  const base = annotated[1]!;
  const existing = traits.find((t) => t === newTrait || new RegExp(`^${base}-\\d*d?\\d+$`).test(t));
  if (!existing) {
    traits.push(newTrait);
    return;
  }
  const valueOf = (t: string): number => {
    const m = /-(\d*)d?(\d+)$/.exec(t);
    return m ? Number(m[1] || 1) * 100 + Number(m[2]) : 0;
  };
  if (valueOf(newTrait) > valueOf(existing)) traits.splice(traits.indexOf(existing), 1, newTrait);
}

export function removeTrait(traits: string[], trait: string): void {
  const index = traits.indexOf(trait);
  if (index !== -1) traits.splice(index, 1);
}

export { ATTRIBUTES };
